import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import ChatPanel from "@/components/ChatPanel";

export default function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Load conversations + honor ?c= query
  useEffect(() => {
    (async () => {
      const items = await refresh();
      const params = new URLSearchParams(location.search);
      const desired = params.get("c");
      if (desired && items.find((c) => c.id === desired)) setActiveId(desired);
      else if (items.length > 0) setActiveId(items[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (!user) return null;
  const viewerRole = user.role;

  const onSelect = (id) => {
    setActiveId(id);
    navigate(`/messages?c=${id}`, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-3">
        <span className="editorial-rule" />Messages
      </div>
      <h1 className="font-serif text-4xl text-mindora-ink leading-tight">
        Your conversations.<br />
        <em className="italic text-mindora-red">Quiet, private, in one place.</em>
      </h1>
      <p className="text-sm text-mindora-muted mt-3 max-w-xl leading-relaxed">
        Talk to your counsellor between sessions — share questions, meeting links, or a quick check-in. Only you two can see what's here.
      </p>
      <div className="mt-10">
        <ChatPanel
          conversations={conversations}
          activeId={activeId}
          onSelectConversation={onSelect}
          onConversationsChanged={refresh}
          viewerRole={viewerRole}
          loading={loading}
        />
      </div>
    </div>
  );
}
